import React, { useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Camera,
  Users,
  BarChart3,
  Settings,
  CreditCard,
  Menu,
  X,
  Home,
  Calendar,
  DollarSign,
} from 'lucide-react';
import { usePhotographer } from '@context/PhotographerContext';
import { useAuth } from '@context/AuthContext';
import Button from '@components/common/Button';
import SetupWidget from '../SetupWidget';

interface DashboardLayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  current?: boolean;
  children?: NavigationItem[];
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { photographer } = usePhotographer();
  const { logout, user } = useAuth();

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    {
      name: 'Events',
      href: '/dashboard/events',
      icon: Calendar,
      children: [{ name: 'All Events', href: '/dashboard/events', icon: Calendar }],
    },
    { name: 'Customers', href: '/dashboard/customers', icon: Users },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
    { name: 'Pricing', href: '/dashboard/pricing', icon: DollarSign },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
    { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
  ];

  const isCurrentPath = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0 px-4">
              <Camera className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-semibold text-gray-900">Pixel Share</span>
            </div>

            {/* Business Name */}
            {photographer?.businessName && (
              <div className="mt-4 px-4">
                <div className="text-sm font-medium text-gray-500">Business</div>
                <div className="text-sm text-gray-900 truncate">{photographer.businessName}</div>
              </div>
            )}

            {/* Navigation */}
            <nav className="mt-8 flex-1 px-2 space-y-1">
              {navigation.map(item => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`
                      group flex items-center px-2 py-2 text-sm font-medium rounded-md
                      ${
                        isCurrentPath(item.href)
                          ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <item.icon
                      className={`
                        mr-3 h-5 w-5 flex-shrink-0
                        ${isCurrentPath(item.href) ? 'text-blue-500' : 'text-gray-400'}
                      `}
                    />
                    {item.name}
                  </Link>

                  {/* Sub-navigation */}
                  {item.children && isCurrentPath(item.href) && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.children.map(subItem => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`
                            group flex items-center px-2 py-1 text-sm rounded-md
                            ${
                              location.pathname === subItem.href
                                ? 'text-blue-700 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                            }
                          `}
                        >
                          <subItem.icon className="mr-2 h-4 w-4" />
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* User profile section */}
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                    <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <Button variant="ghost" size="small" onClick={handleLogout} className="ml-2">
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Mobile navigation content - same as desktop */}
            <div className="flex-shrink-0 flex items-center px-4">
              <Camera className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-semibold text-gray-900">Pixel Share</span>
            </div>

            <div className="mt-5 flex-1 h-0 overflow-y-auto">
              <nav className="px-2 space-y-1">
                {navigation.map(item => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      group flex items-center px-2 py-2 text-base font-medium rounded-md
                      ${
                        isCurrentPath(item.href)
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="mr-4 h-6 w-6 flex-shrink-0" />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        <div className="lg:hidden">
          <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-2">
            <div className="flex items-center">
              <button
                type="button"
                className="-mr-3 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="ml-2">
                <Camera className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <Button variant="ghost" size="small" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none">{children}</main>
      </div>
      <SetupWidget />
    </div>
  );
};

export default DashboardLayout;
